import { useOpenS3File } from '@/components/hooks/useOpenS3File';
import { FlexColumnDiv } from '@/components/shared/components/common.StyledComponents';
import { MaskField } from '@/components/shared/components/MaskedField';
import { formatEIN } from '@/components/shared/utils/utils';
import { ButtonVariants } from '@triumph/design-system';
import { isEmpty } from 'es-toolkit/compat';
import { FileButton } from '../Applicant.StyledComponents';
import { BusinessInfoProps, BusinessTypes } from '../Applicant.types';
import { getTaxClassificationLabel } from '../Applicant.utils';
import { ApplicantInfoCard, ApplicantInfoRow } from './ApplicantInfoCard';

type BusinessInformationProps = BusinessInfoProps & {
    isBusinessAPerson: boolean | null;
    isSubmission?: boolean;
};

export const BusinessInformation = ({
    legalName,
    stateOfOrganization,
    dbaName,
    federalTaxId,
    typeOfBusiness,
    taxClassification,
    corpPresidentLegalName,
    isBusinessAPerson,
    files,
    isSubmission = false,
}: BusinessInformationProps) => {
    const { handleFileClick, getFileDragProps } = useOpenS3File();

    // Business Documents should only surface files the applicant uploaded,
    // which are flagged as external.
    const externalFiles = files?.filter((file) => file.isExternal);

    return (
        <ApplicantInfoCard title="Business Information">
            <ApplicantInfoRow
                label="Is Business a Person"
                value={isBusinessAPerson == null ? null : isBusinessAPerson ? 'Yes' : 'No'}
                // Calculated per request on the live application; a submission never records it.
                isLive={!isSubmission}
                data-testid="isBusinessAPerson"
            />
            <ApplicantInfoRow
                label="Legal Business Name"
                value={legalName}
                data-testid="legalName"
            />
            <ApplicantInfoRow
                label="State of Organization/Incorporation"
                value={stateOfOrganization}
                data-testid="stateOfOrganization"
            />
            <ApplicantInfoRow
                label="Doing Business As (DBA) Name"
                value={dbaName}
                data-testid="dbaName"
            />
            <ApplicantInfoRow
                label="Federal Tax ID/EIN"
                value={
                    isEmpty(federalTaxId) ? null : <MaskField value={formatEIN(federalTaxId)} />
                }
                data-testid="federalTaxId"
            />
            <ApplicantInfoRow
                label="Type of Business"
                value={BusinessTypes[typeOfBusiness as keyof typeof BusinessTypes]}
                data-testid="typeOfBusiness"
            />
            <ApplicantInfoRow
                label="Tax Classification"
                value={getTaxClassificationLabel(taxClassification)}
                data-testid="taxClassification"
            />
            <ApplicantInfoRow
                label="Legal Name of Corporation President"
                value={corpPresidentLegalName}
                data-testid="corpPresidentLegalName"
            />
            <ApplicantInfoRow
                label="Business Documents"
                value={
                    externalFiles &&
                    externalFiles.length > 0 && (
                        <FlexColumnDiv>
                            {externalFiles?.map((file) => (
                                <FileButton
                                    key={file.id}
                                    onClick={() => handleFileClick(file.id)}
                                    {...getFileDragProps(file.id, file.filename)}
                                    variant={ButtonVariants.tertiary}
                                    // A plain prop, so a compare sees a same-name replacement.
                                    data-file-id={file.id}
                                >
                                    {file.filename}
                                </FileButton>
                            ))}
                        </FlexColumnDiv>
                    )
                }
                data-testid="files"
            />
        </ApplicantInfoCard>
    );
};
